import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Divider,
} from '@mui/material';
import { GlassBackdrop, GlassSurface } from '../../components/GlassDashboard';
import {
  Description as DevisIcon,
  CheckCircle as ValidatedIcon,
  HourglassEmpty as PendingIcon,
  Cancel as CancelledIcon,
  LocalShipping as BLIcon,
  Done as LivreIcon,
} from '@mui/icons-material';
import { devisAPI, bonsLivraisonAPI, commercialDashboardAPI, CommercialDashboardStats } from '../../services/api';
import { Devis } from '../../types/devis';
import { BonLivraison, BonLivraisonStatus } from '../../types/bonLivraison';
import { useAuth } from '../../contexts/AuthContext';

const CommercialOverview: React.FC = () => {
  const [devisList, setDevisList] = useState<Devis[]>([]);
  const [blList, setBlList] = useState<BonLivraison[]>([]);
  const [stats, setStats] = useState<CommercialDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Check if user is Manager or Developer
  const canSeeTotalMontant = user?.role && ['Manager', 'Developer', 'admin'].includes(user.role);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, devisResponse, blResponse] = await Promise.all([
          commercialDashboardAPI.getStats(),
          devisAPI.getPaginated(0, 5),
          bonsLivraisonAPI.getPaginated(0, 5),
        ]);
        setStats(statsData);
        setDevisList(devisResponse.items);
        setBlList(blResponse.items);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Devis stats (server-aggregated)
  const totalDevis = stats?.devis.total ?? 0;
  const devisEnCoursCount = stats?.devis.en_cours ?? 0;
  const devisValideCount = stats?.devis.valide ?? 0;
  const devisAnnuleCount = stats?.devis.annule ?? 0;
  const totalMontant = stats?.devis.montant_total ?? 0;

  // BL stats (server-aggregated)
  const totalBL = stats?.bons_livraison.total ?? 0;
  const blEnCoursCount = stats?.bons_livraison.en_cours ?? 0;
  const blLivreCount = stats?.bons_livraison.livre ?? 0;
  const blAnnuleCount = stats?.bons_livraison.annule ?? 0;

  const devisStatsCards = [
    {
      title: 'Total Devis',
      value: totalDevis,
      icon: <DevisIcon sx={{ fontSize: 40 }} />,
      color: '#4CAF50',
    },
    {
      title: 'En Cours',
      value: devisEnCoursCount,
      icon: <PendingIcon sx={{ fontSize: 40 }} />,
      color: '#FF9800',
    },
    {
      title: 'Validés',
      value: devisValideCount,
      icon: <ValidatedIcon sx={{ fontSize: 40 }} />,
      color: '#2196F3',
    },
    {
      title: 'Annulés',
      value: devisAnnuleCount,
      icon: <CancelledIcon sx={{ fontSize: 40 }} />,
      color: '#F44336',
    },
  ];

  const blStatsCards = [
    {
      title: 'Total BL',
      value: totalBL,
      icon: <BLIcon sx={{ fontSize: 40 }} />,
      color: '#4CAF50',
    },
    {
      title: 'En Cours',
      value: blEnCoursCount,
      icon: <PendingIcon sx={{ fontSize: 40 }} />,
      color: '#FF9800',
    },
    {
      title: 'Livrés',
      value: blLivreCount,
      icon: <LivreIcon sx={{ fontSize: 40 }} />,
      color: '#2196F3',
    },
    {
      title: 'Annulés',
      value: blAnnuleCount,
      icon: <CancelledIcon sx={{ fontSize: 40 }} />,
      color: '#F44336',
    },
  ];

  return (
    <GlassBackdrop>
      <Typography variant="h4" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
        Tableau de Bord Commercial
      </Typography>

      {/* Devis Section */}
      <Typography variant="h6" gutterBottom sx={{ color: '#4CAF50', mt: 2 }}>
        Devis
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {devisStatsCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <GlassSurface accentColor={card.color} sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {card.title}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {loading ? '...' : card.value}
                  </Typography>
                </Box>
                <Box sx={{ color: card.color }}>{card.icon}</Box>
              </Box>
            </GlassSurface>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Bons de Livraison Section */}
      <Typography variant="h6" gutterBottom sx={{ color: '#4CAF50' }}>
        Bons de Livraison
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {blStatsCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <GlassSurface accentColor={card.color} sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {card.title}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {loading ? '...' : card.value}
                  </Typography>
                </Box>
                <Box sx={{ color: card.color }}>{card.icon}</Box>
              </Box>
            </GlassSurface>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {canSeeTotalMontant && (
          <Grid item xs={12} md={6}>
            <GlassSurface accentColor="#4CAF50" sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#4CAF50' }}>
                Montant Total des Devis
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 600 }}>
                {loading ? '...' : `${totalMontant.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH`}
              </Typography>
            </GlassSurface>
          </Grid>
        )}
        <Grid item xs={12} md={canSeeTotalMontant ? 6 : 12}>
          <GlassSurface accentColor="#4CAF50" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#4CAF50' }}>
              Devis Récents
            </Typography>
            {loading ? (
              <Typography>Chargement...</Typography>
            ) : devisList.length === 0 ? (
              <Typography color="textSecondary">Aucun devis créé</Typography>
            ) : (
              <Box>
                {devisList.slice(0, 5).map((devis) => (
                  <Box
                    key={devis.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      py: 1,
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2">{devis.reference}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {devis.client_name}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {Number(devis.montant_total).toLocaleString('fr-FR')} DH
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </GlassSurface>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <GlassSurface accentColor="#4CAF50" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#4CAF50' }}>
              Bons de Livraison Récents
            </Typography>
            {loading ? (
              <Typography>Chargement...</Typography>
            ) : blList.length === 0 ? (
              <Typography color="textSecondary">Aucun bon de livraison créé</Typography>
            ) : (
              <Box>
                {blList.slice(0, 5).map((bl) => (
                  <Box
                    key={bl.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      py: 1,
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2">{bl.reference}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {bl.client_name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: bl.status === BonLivraisonStatus.LIVRE
                          ? '#4CAF50'
                          : bl.status === BonLivraisonStatus.ANNULE
                            ? '#F44336'
                            : '#FF9800',
                        fontWeight: 600
                      }}
                    >
                      {bl.status === BonLivraisonStatus.LIVRE
                        ? 'Livré'
                        : bl.status === BonLivraisonStatus.ANNULE
                          ? 'Annulé'
                          : 'En cours'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </GlassSurface>
        </Grid>
      </Grid>
    </GlassBackdrop>
  );
};

export default CommercialOverview;
